import React, { useState } from 'react';
import { Sparkles, ExternalLink } from 'lucide-react';
import { POSTERS } from '../../config/posters';
import { PosterModal } from './KnowledgePosters';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';
import { tSurf } from '../../config/newSurfacesTranslations';

/**
 * PosterThumbStrip — compact horizontal strip of poster thumbnails.
 *
 * Meant for embedding on secondary surfaces (Learn page, calculator
 * pages) where the full 3-up gallery would be too heavy. Reuses the
 * shared `PosterModal` from `KnowledgePosters.jsx` so the click-to-expand
 * viewer is identical everywhere — PNG/PDF download, WhatsApp share,
 * source attribution.
 *
 * Props:
 *   ids?        — array of poster ids to include. Omit to include all live.
 *   title?      — section title (defaults to "Related knowledge posters").
 *   subtitle?   — one-liner shown under the title.
 *   compact?    — narrower thumbnails, useful inside a sidebar.
 *   testid?     — data-testid prefix (default: "poster-thumb-strip").
 */
export const PosterThumbStrip = ({
  ids,
  // featuredIds — pin these poster ids to the front of the list so they
  // are surfaced first (visually highlighted with a small badge). Used
  // on Inheritance / Will pages where 2 posters were previously teased
  // separately near the top; we now feature them at the top of the
  // bottom compact strip instead.
  featuredIds,
  title = 'Related knowledge posters',
  subtitle = 'Tap any thumbnail to view, download or share.',
  compact = false,
  testid = 'poster-thumb-strip',
}) => {
  const [active, setActive] = useState(null);
  const { selectedLanguage } = useZakatGlobal();
  const lang = selectedLanguage?.code || 'en';
  const ts = (path) => tSurf(lang, path);

  const idsKey = (ids || []).join('|');
  const featuredKey = (featuredIds || []).join('|');

  // If featuredIds given, hoist them to the front and mark them.
  const items = React.useMemo(() => {
    const idList = idsKey ? idsKey.split('|') : [];
    const featuredList = featuredKey ? featuredKey.split('|') : [];
    const baseItems = idList.length > 0
      ? idList.map((id) => POSTERS.find((p) => p.id === id)).filter(Boolean)
      : POSTERS.filter((p) => p.status === 'live');
    if (featuredList.length === 0) return baseItems;
    const featured = featuredList
      .map((id) => baseItems.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => ({ ...p, __featured: true }));
    const rest = baseItems.filter((p) => !featuredList.includes(p.id));
    return [...featured, ...rest];
  }, [idsKey, featuredKey]);

  // Lock scroll while modal is open
  React.useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);

  // Escape closes
  React.useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  if (items.length === 0) return null;

  const thumbSize = compact ? 'w-14 h-14' : 'w-16 h-16 sm:w-20 sm:h-20';

  return (
    <section
      data-testid={testid}
      className="rounded-2xl border border-stone-200 bg-gradient-to-br from-[#FAF8F5] to-stone-50/60 p-4 sm:p-5"
    >
      <div className="flex items-start gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gold-100 border border-gold-300 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-gold-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-heading text-[#0D1520] leading-tight">{title}</h3>
          <p className="text-[11.5px] text-stone-500 mt-0.5 leading-snug">{subtitle}</p>
        </div>
      </div>

      <ul
        className="flex gap-2.5 sm:gap-3 overflow-x-auto scrollbar-none -mx-1 px-1 pb-1"
        data-testid={`${testid}-list`}
      >
        {items.map((p) => (
          <li key={p.id} className="shrink-0">
            <button
              type="button"
              onClick={() => setActive(p)}
              data-testid={`${testid}-thumb-${p.id}`}
              aria-label={`Open poster: ${p.title}`}
              className="group flex flex-col items-start w-[110px] sm:w-[128px] text-start hover:-translate-y-0.5 transition-transform"
            >
              <div className={`relative ${thumbSize} w-full aspect-[3/4] overflow-hidden rounded-lg border ${p.__featured ? 'border-gold-500 ring-2 ring-gold-400/50' : 'border-stone-200'} bg-stone-100 group-hover:border-gold-400 group-hover:shadow-md transition-all`}>
                <img
                  src={p.thumb || p.image}
                  alt={`${p.title} — ${p.excerpt}`}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                />
                <span className="absolute top-1 start-1 inline-flex items-center rounded-sm bg-[#EDF5F1] text-[#0D1520] text-[8.5px] font-mono uppercase tracking-[0.14em] font-semibold px-1 py-0.5 backdrop-blur-sm">
                  {p.series} · {String(p.seriesNum).padStart(2, '0')}
                </span>
                {p.__featured && (
                  <span className="absolute top-1 end-1 inline-flex items-center rounded-sm bg-gold-500 text-[#0D1520] text-[8.5px] font-mono uppercase tracking-[0.14em] font-bold px-1 py-0.5 shadow">
                    ★ Featured
                  </span>
                )}
                <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-[#EDF5F1] via-[#EDF5F1] to-transparent flex items-end justify-center p-1.5">
                  <span className="inline-flex items-center gap-1 text-[#0D1520] text-[9.5px] font-semibold rounded-full bg-[#EDF5F1] px-2 py-0.5 border border-gold-500/40">
                    View <ExternalLink className="w-2.5 h-2.5" />
                  </span>
                </span>
              </div>
              <span className="mt-1.5 text-[11px] font-medium text-[#0D1520] line-clamp-2 leading-snug">
                {p.title}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {active && <PosterModal poster={active} onClose={() => setActive(null)} ts={ts} />}
    </section>
  );
};

export default PosterThumbStrip;
